import React from "react";
import { Link } from "react-router-dom";
import {
    FileText,
    Truck,
    Bell,
    ClipboardCheck,
    Activity,
    Receipt,
    Container,
    XCircle,
    Users,
    ExternalLink,
    ChevronRight,
} from "lucide-react";
import { resolveReportHref } from "@/pages/reports/reportUtils";
import { getReportModule } from "@/pages/reports/reportModules";
import { reportAccent, reportCard, reportCardHover, reportMutedText } from "@/components/reports/layout/reportTheme";

const ICON_MAP = {
    fileText: FileText,
    truck: Truck,
    bell: Bell,
    clipboard: ClipboardCheck,
    activity: Activity,
    receipt: Receipt,
    container: Container,
    cancel: XCircle,
    users: Users,
};

const pickIcon = (id = "") => {
    if (id.includes("truck")) return Truck;
    if (id.includes("container") || id.includes("tracking")) return Container;
    if (id.includes("eta") || id.includes("alert")) return Bell;
    if (id.includes("sales") || id.includes("quote")) return Users;
    if (id.includes("accounting") || id.includes("profit") || id.includes("demurrage")) return Receipt;
    if (id.includes("cancel")) return XCircle;
    if (id.includes("audit") || id.includes("status")) return ClipboardCheck;
    if (id.includes("active")) return Activity;
    return FileText;
};

const ReportCatalogCard = ({ report, moduleId, mode }) => {
    const mod = getReportModule(moduleId);
    const { href, external } = resolveReportHref(report, {
        mode,
        resolveLegacyHref: mod?.resolveLegacyHref,
    });
    const Icon = ICON_MAP[report.icon] || pickIcon(report.id);
    const badge = report.badge || "LIVE";
    const badgeClass =
        badge === "PRO"
            ? "bg-violet-100 text-violet-700 border-violet-200/80 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30"
            : "bg-emerald-100 text-emerald-700 border-emerald-200/80 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30";

    const content = (
        <div className="flex items-start gap-4">
            <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl
                           bg-gradient-to-br from-blue-600 to-indigo-600 text-white
                           shadow-[0_4px_14px_-4px_rgba(37,99,235,0.5)]
                           ring-1 ring-blue-500/20 group-hover:scale-105 transition-transform duration-200"
            >
                <Icon className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{report.label}</h3>
                    <span
                        className={`rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badgeClass}`}
                    >
                        {badge}
                    </span>
                </div>
                {report.description && (
                    <p className={`mt-1.5 text-xs leading-relaxed line-clamp-2 ${reportMutedText}`}>
                        {report.description}
                    </p>
                )}
                <span
                    className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold
                                opacity-80 group-hover:opacity-100 group-hover:underline ${reportAccent}`}
                >
                    Open report
                    {external ? <ExternalLink className="h-3 w-3" /> : <ChevronRight className="h-3.5 w-3.5" />}
                </span>
            </div>
        </div>
    );

    const cardClass =
        `group block p-4 sm:p-[18px] no-underline ${reportCard} ${reportCardHover}`;

    if (external) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClass}
                data-testid={`report-card-${report.id}`}
            >
                {content}
            </a>
        );
    }

    return (
        <Link to={href} className={cardClass} data-testid={`report-card-${report.id}`}>
            {content}
        </Link>
    );
};

export default ReportCatalogCard;
